import { Link } from '@tanstack/react-router';
import { useScreens } from '../api/screens.ts';
import { useOrg } from '../api/org.ts';
import { Loading, ErrorState, Meter, StatusDot, Badge } from '../components/ui.tsx';
import { formatMb, formatElapsed, formatRelative } from '../lib/format.ts';
import { WelcomeBanner } from '../onboarding/WelcomeBanner.tsx';
import { GettingStartedCard } from '../onboarding/GettingStartedCard.tsx';
import { NeedsAttentionCard } from './NeedsAttentionCard.tsx';

/** Small inline check-circle (no emoji) for the "all online" reassurance line. */
function CheckCircleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ color: 'var(--ok)', flex: 'none' }}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.5l2.5 2.5 4.5-5" />
    </svg>
  );
}

export function DashboardPage() {
  const screens = useScreens();
  const org = useOrg();

  if (screens.isLoading || org.isLoading) return <div className="page"><Loading /></div>;
  if (screens.isError || !screens.data || !org.data)
    return <div className="page"><ErrorState message="Could not load the dashboard." onRetry={() => screens.refetch()} /></div>;

  const rows = screens.data;
  const online = rows.filter((s) => s.status === 'online');
  const offline = rows.filter((s) => s.status === 'offline');
  const neverPaired = rows.filter((s) => s.status === 'never_paired');
  const now = Date.now();
  const offlineSorted = [...offline].sort((a, b) => (a.last_seen_at ?? 0) - (b.last_seen_at ?? 0));

  const { usage, limits } = org.data;
  const storagePct = limits.max_storage_mb > 0 ? (usage.storage_mb / limits.max_storage_mb) * 100 : 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <div className="subtitle">{org.data.name} · fleet at a glance</div>
        </div>
      </div>

      <WelcomeBanner />
      <GettingStartedCard />
      <NeedsAttentionCard />

      <div className="grid cols-4 mb-4">
        <div className="card stat">
          <span className="label">Online</span>
          <span className="value" style={{ color: 'var(--ok)' }}>
            {online.length}
            <small> / {rows.length}</small>
          </span>
        </div>
        <div className="card stat">
          <span className="label">Offline</span>
          <span className="value" style={{ color: offline.length ? 'var(--danger)' : undefined }}>
            {offline.length}
          </span>
        </div>
        <div className="card stat">
          <span className="label">Not paired</span>
          <span className="value">{neverPaired.length}</span>
        </div>
        <div className="card stat">
          <span className="label">Screens used</span>
          <span className="value">
            {usage.screens}
            <small> / {limits.max_screens}</small>
          </span>
          <Meter value={usage.screens} max={limits.max_screens} />
        </div>
      </div>

      <div className="grid cols-2">
        <div className="card card-pad">
          <div className="card-title">Offline screens</div>
          {offlineSorted.length === 0 ? (
            <p className="text-dim all-online">
              <CheckCircleIcon />
              Everything paired is online.
            </p>
          ) : (
            <table className="data" style={{ width: '100%' }}>
              <tbody>
                {offlineSorted.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <Link to="/screens" search={{ screen: s.id }} className="cell-strong">
                        {s.name}
                      </Link>
                      <div className="cell-dim">{s.location}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge tone="warn">
                        {s.last_seen_at ? `down ${formatElapsed(now - s.last_seen_at)}` : 'never seen'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-title">Storage</div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="text-dim">
              {formatMb(usage.storage_mb)} of {formatMb(limits.max_storage_mb)}
            </span>
            <span className="text-dim">{Math.round(storagePct)}%</span>
          </div>
          <div className="mt-2">
            <Meter value={usage.storage_mb} max={limits.max_storage_mb} />
          </div>
          {storagePct >= 75 && (
            <p className="hint mt-4">
              Approaching your plan limit — contact SeenLabs to upgrade.
            </p>
          )}
        </div>
      </div>

      <div className="card card-pad mt-4">
        <div className="card-title">What&apos;s playing now</div>
        <div className="table-wrap" style={{ border: 'none' }}>
          <table className="data">
            <thead>
              <tr>
                <th>Screen</th>
                <th>Status</th>
                <th>Now playing</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td className="cell-strong">
                    <Link to="/screens" search={{ screen: s.id }}>
                      {s.name}
                    </Link>
                  </td>
                  <td>
                    <StatusDot status={s.status} />
                  </td>
                  <td>
                    {s.last_state?.current_playlist_name ?? <span className="cell-dim">—</span>}
                  </td>
                  <td className="cell-dim">{formatRelative(s.last_seen_at, now)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
